import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FileButton,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifySuccess } from "../../utils/notify";
import { IconAlertCircle, IconFile, IconUpload } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { selectedRowStyle } from "../formTemplates/selectedRowStyle";
import { SalonDocument } from "../../types/salonDocument";
import { makeTempId } from "../../utils/makeTempId";

interface AssignPreAppointmentDocumentModalProps {
  opened: boolean;
  onClose: () => void;
  onAssign: (
    picks: Array<{ source_id: string; name: string }>,
  ) => Promise<void> | void;
  isAssigning?: boolean;
  /** salon_documents UUIDs already attached — hidden from the picker. */
  existingDocumentIds?: ReadonlySet<string>;
}

const EMPTY_IDS: ReadonlySet<string> = new Set();

const documentDisplayName = (d: SalonDocument): string =>
  d.displayName?.trim() || d.documentKey || "Untitled document";

/**
 * Documents-only picker for pre-appointment attachments. Trimmed sibling of
 * servicePostAppointment/AssignPostAppointmentAttachmentModal — pre-visit
 * attachments carry no template sources, so there are no tabs.
 */
export const AssignPreAppointmentDocumentModal: React.FC<
  AssignPreAppointmentDocumentModalProps
> = ({
  opened,
  onClose,
  onAssign,
  isAssigning = false,
  existingDocumentIds = EMPTY_IDS,
}) => {
  const { listSalonDocuments, uploadSalonDocument } = useApp() as {
    listSalonDocuments: (
      salonId: string,
    ) => Promise<{ documents: SalonDocument[] }>;
    uploadSalonDocument: (
      salonId: string,
      documentKey: string,
      file: File | Blob,
      contentType: string,
      metadata?: Record<string, unknown>,
      clientId?: string,
      displayName?: string,
    ) => Promise<{ document?: SalonDocument } | unknown>;
  };
  const { businessInfo } = useSalon() as {
    businessInfo: { id: string } | null;
  };

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [documents, setDocuments] = useState<SalonDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const refreshDocuments = useCallback(
    async (salonId: string): Promise<SalonDocument[]> => {
      const res = await listSalonDocuments(salonId);
      const next = (res?.documents ?? []).filter((d) => !d.clientId);
      setDocuments(next);
      return next;
    },
    [listSalonDocuments],
  );

  const handleUploadDocument = useCallback(
    async (file: File | null) => {
      if (!file || !businessInfo?.id) return;
      const salonId = businessInfo.id;
      const newKey = makeTempId("doc");
      try {
        setUploading(true);
        setDocsError(null);
        await uploadSalonDocument(
          salonId,
          newKey,
          file,
          file.type || "application/octet-stream",
          undefined,
          undefined, // salon-level (no clientId) — required for pre-appointment attachments
          file.name,
        );
        const next = await refreshDocuments(salonId);
        const created = next.find((d) => d.documentKey === newKey);
        if (created) {
          setSelectedIds((prev) =>
            prev.includes(created.documentUuid)
              ? prev
              : [...prev, created.documentUuid],
          );
        }
        notifySuccess(`${file.name} is ready to attach.`, {
          title: "Document uploaded",
        });
      } catch (err) {
        setDocsError(
          err instanceof Error ? err.message : "Upload failed. Try again.",
        );
      } finally {
        setUploading(false);
      }
    },
    [businessInfo?.id, uploadSalonDocument, refreshDocuments],
  );

  useEffect(() => {
    if (!opened) {
      setSelectedIds([]);
      setSearch("");
      setDocsError(null);
      return;
    }
    if (!businessInfo?.id) return;
    let cancelled = false;
    (async () => {
      try {
        setDocsLoading(true);
        setDocsError(null);
        await refreshDocuments(businessInfo.id);
      } catch (err) {
        if (cancelled) return;
        setDocsError(
          err instanceof Error ? err.message : "Could not load documents.",
        );
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, businessInfo?.id, refreshDocuments]);

  const availableDocs = useMemo(
    () => documents.filter((d) => !existingDocumentIds.has(d.documentUuid)),
    [documents, existingDocumentIds],
  );

  const activeDocs = search.trim()
    ? availableDocs.filter((d) =>
        documentDisplayName(d).toLowerCase().includes(search.toLowerCase()),
      )
    : availableDocs;

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of availableDocs) {
      map.set(d.documentUuid, documentDisplayName(d));
    }
    return map;
  }, [availableDocs]);

  // On a partial-success link the picker stays open and the just-linked docs
  // move into existingDocumentIds (dropping out of availableDocs/nameById), but
  // they'd linger in selectedIds and inflate the footer count / button label.
  // Prune any selected id that's no longer available; failed picks remain in
  // availableDocs and stay selected for a retry. Search doesn't prune —
  // nameById derives from the unfiltered availableDocs.
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => nameById.has(id)));
  }, [nameById]);

  const handleAssign = async () => {
    if (selectedIds.length === 0) return;
    const picks = selectedIds
      .filter((id) => nameById.has(id))
      .map((id) => ({ source_id: id, name: nameById.get(id) ?? id }));
    await onAssign(picks);
  };

  const buttonLabel =
    selectedIds.length > 1
      ? `Attach ${selectedIds.length} documents`
      : "Attach";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={700}>Attach pre-appointment documents</Text>}
      size="lg"
      centered
    >
      <Stack gap="md">
        <TextInput
          placeholder="Search documents by name"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          size="xs"
        />

        {docsError && (
          <Alert color="red" icon={<IconAlertCircle size={14} />}>
            {docsError}
          </Alert>
        )}

        <Group justify="flex-end" align="center">
          <FileButton
            onChange={handleUploadDocument}
            accept="application/pdf,image/png,image/jpeg,image/heic,image/heif,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          >
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant="light"
                color="var(--brand-teal)"
                leftSection={<IconUpload size={14} />}
                loading={uploading}
                disabled={!businessInfo?.id || uploading}
              >
                {uploading ? "Uploading…" : "Upload document"}
              </Button>
            )}
          </FileButton>
        </Group>

        <Box
          style={{
            maxHeight: 360,
            overflowY: "auto",
            padding: 4,
            border: "1px solid var(--mantine-color-gray-2)",
            borderRadius: 6,
          }}
        >
          {docsLoading ? (
            <Group justify="center" py="md">
              <Loader size="xs" />
            </Group>
          ) : activeDocs.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" p="md">
              {search.trim()
                ? "No documents match."
                : documents.length > 0
                  ? "All documents are already attached."
                  : "No documents uploaded yet. Use Upload document above to add one."}
            </Text>
          ) : (
            <Checkbox.Group value={selectedIds} onChange={setSelectedIds}>
              <Stack gap={0}>
                {activeDocs.map((d) => {
                  const id = d.documentUuid;
                  const name = documentDisplayName(d);
                  return (
                    <Box
                      key={id}
                      p="xs"
                      onClick={() =>
                        setSelectedIds((prev) =>
                          prev.includes(id)
                            ? prev.filter((k) => k !== id)
                            : [...prev, id],
                        )
                      }
                      style={selectedRowStyle(selectedIds.includes(id))}
                    >
                      <Group gap="xs" wrap="nowrap" align="flex-start">
                        <Checkbox
                          value={id}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <IconFile
                          size={18}
                          color="var(--mantine-color-blue-6)"
                        />
                        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" fw={600} truncate>
                            {name}
                          </Text>
                          {d.currentContentType && (
                            <Text size="xs" c="dimmed">
                              {d.currentContentType}
                            </Text>
                          )}
                        </Stack>
                      </Group>
                    </Box>
                  );
                })}
              </Stack>
            </Checkbox.Group>
          )}
        </Box>

        <Group justify="space-between" align="center">
          <Text size="xs" c="dimmed">
            {selectedIds.length === 0
              ? "Select one or more documents"
              : `${selectedIds.length} document${selectedIds.length > 1 ? "s" : ""} selected`}
          </Text>
          <Group gap="xs">
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="var(--brand-teal)"
              disabled={selectedIds.length === 0}
              loading={isAssigning}
              onClick={handleAssign}
            >
              {buttonLabel}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default AssignPreAppointmentDocumentModal;
